// Iter86 / Phase 3F — Shared Snapshot Scan Kit.
//
// Pure presentational + control component shared by the
// per-module controlled auto-scan panels (BCCS, Iron Condor;
// BPCSAutoScanPanel uses its own inline equivalent and stays
// pinned per Iter85). The host panel owns:
//   * the actual scanner endpoint URL,
//   * payload construction,
//   * the axios call,
//   * result rendering.
//
// The kit owns ONLY the visual scaffold + control surface around
// the snapshot:
//   * snapshot id + age + stale chip,
//   * symbol picker grid with module-aware BLOCKED tagging,
//   * select-all / clear-all,
//   * max symbols / min IVR / freshness threshold inputs,
//   * Run + Clear buttons (wired to host callbacks),
//   * Last scanned Ns ago stamp (in-memory only),
//   * error block + safety banner chips.
//
// HARD CONSTRAINTS (asserted by Iter86 tests):
//  * No HTTP. No `axios`. No `fetch(`. No `XMLHttpRequest`.
//  * No `useEffect`. No `useState` either — the kit is fully
//    controlled by the host panel's props.
//  * No `localStorage` / `sessionStorage` / `IndexedDB` / cookies.
//  * No module endpoint URL constants. No knowledge of
//    `bpcs-preview` / `bccs-preview` / `iron-condor-preview` /
//    `cfm-*-preview` / `/forge/candidates` / `/market-scanner/*`.
//  * No ranking / winner / best / preferred / auto-select
//    language outside the safety-banner labels passed in by the
//    host (which are themselves disclaimers).

import React from "react";
import {
  ShieldAlert, Play, Eraser, Clock, AlertTriangle,
} from "lucide-react";

export default function SnapshotScanKit({
  // Snapshot summary
  snapshot,
  rows,
  staleSecondsCfg,
  ageSeconds,        // (iso) => number|null  — pure helper from host
  // Picker state
  selected,
  onToggleSymbol,
  onSelectAll,
  onClearAll,
  chainAvailability, // (row) => { ok: bool, blocked_reason?: string,
                     //            puts?: number, calls?: number }
  // Controls
  maxSymbols, setMaxSymbols,
  minIvr,     setMinIvr,
  // Actions
  loading, onRun, onClear, errorMsg,
  runLabel,
  // Last-scan stamp
  lastScannedAt,
  // Surface metadata
  testidPrefix,
  safetyLabels,      // [{ testid, text }] — full disclaimer set
}) {
  const ageS = ageSeconds(snapshot?.as_of);
  const stale = ageS !== null && ageS > staleSecondsCfg;
  const lastScannedAge = ageSeconds(lastScannedAt);
  const tid = testidPrefix;

  return (
    <>
      {/* ── Safety banner ─────────────────────────────────── */}
      <div
        data-testid={`${tid}-banner`}
        className="px-4 py-2 border-b border-[#26262B] bg-[#FFB800]/5 text-[#FFB800] text-[10px] font-mono flex items-center gap-3 flex-wrap"
      >
        <ShieldAlert size={12} />
        {safetyLabels.map((s, i) => (
          <React.Fragment key={s.testid}>
            {i > 0 && <span className="text-[#FFB800]/50">·</span>}
            <span data-testid={s.testid}>{s.text}</span>
          </React.Fragment>
        ))}
      </div>

      {/* ── Snapshot status ───────────────────────────────── */}
      {!snapshot && (
        <div
          data-testid={`${tid}-no-snapshot`}
          className="px-4 py-3 text-[11px] font-mono text-[#FFB800] border-b border-[#26262B] flex items-start gap-2"
        >
          <AlertTriangle size={12} className="shrink-0 mt-0.5" />
          <span>
            No in-memory snapshot yet. Build a fresh market
            snapshot in the Market Snapshot Builder, then return
            here to run this scan against it.
          </span>
        </div>
      )}

      {snapshot && (
        <div
          data-testid={`${tid}-snapshot-summary`}
          className="px-4 py-2 border-b border-[#26262B] bg-[#00FF88]/5 text-[10px] font-mono flex items-center gap-3 flex-wrap text-[#00FF88]"
        >
          <span data-testid={`${tid}-snapshot-id`} className="text-[#00FF88]/80">
            snapshot {snapshot.snapshot_id?.slice(0, 8)}
          </span>
          <span className="text-[#00FF88]/40">·</span>
          <span data-testid={`${tid}-snapshot-age`} className="inline-flex items-center gap-1 text-[#00FF88]/80">
            <Clock size={10} />
            {ageS != null ? `${ageS}s ago` : "—"}
          </span>
          {stale && (
            <span
              data-testid={`${tid}-stale-warning`}
              className="px-1.5 py-0.5 rounded-sm border border-[#FFB800]/40 bg-[#FFB800]/5 text-[#FFB800] uppercase tracking-[0.12em] inline-flex items-center gap-1"
            >
              <AlertTriangle size={9} />
              Snapshot stale — refresh before relying on this scan.
            </span>
          )}
          {snapshot.rate_limited && (
            <span
              data-testid={`${tid}-rate-limited`}
              className="px-1.5 py-0.5 rounded-sm border border-[#FFB800]/40 bg-[#FFB800]/5 text-[#FFB800] uppercase tracking-[0.12em]"
            >
              RATE_LIMITED
            </span>
          )}
          {snapshot.partial && (
            <span
              data-testid={`${tid}-partial`}
              className="px-1.5 py-0.5 rounded-sm border border-[#FFB800]/40 bg-[#FFB800]/5 text-[#FFB800] uppercase tracking-[0.12em]"
            >
              PARTIAL
            </span>
          )}
        </div>
      )}

      {/* ── Symbol picker + settings ─────────────────────── */}
      {snapshot && (
        <div className="px-4 py-3 border-b border-[#26262B] space-y-3">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="text-[10px] font-mono uppercase tracking-[0.14em] text-[#8A8A93]">
              Snapshot symbols
            </span>
            <button
              type="button"
              data-testid={`${tid}-select-all`}
              onClick={onSelectAll}
              className="px-2 py-0.5 rounded-sm border border-[#26262B] text-[9px] font-mono uppercase tracking-[0.12em] text-[#8A8A93] hover:text-[#E0E0E0] hover:border-[#3F3F47]"
            >
              Select all
            </button>
            <button
              type="button"
              data-testid={`${tid}-clear-all`}
              onClick={onClearAll}
              className="px-2 py-0.5 rounded-sm border border-[#26262B] text-[9px] font-mono uppercase tracking-[0.12em] text-[#8A8A93] hover:text-[#E0E0E0] hover:border-[#3F3F47]"
            >
              Clear all
            </button>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-2">
            {rows.map(r => {
              const avail = chainAvailability(r);
              const puts = avail.puts ?? 0;
              const calls = avail.calls ?? 0;
              return (
                <label
                  key={r.symbol}
                  data-testid={`${tid}-symbol-${r.symbol}`}
                  className="flex items-center gap-2 px-2 py-1 rounded-sm border border-[#26262B] bg-[#08080A] text-[10px] font-mono cursor-pointer hover:border-[#22D3CC]/40"
                >
                  <input
                    type="checkbox"
                    data-testid={`${tid}-symbol-${r.symbol}-checkbox`}
                    checked={!!selected[r.symbol]}
                    onChange={() => onToggleSymbol(r.symbol)}
                  />
                  <span className="text-[#E0E0E0] font-bold">{r.symbol}</span>
                  <span className="text-[#5F5F66]">PUTS {puts}</span>
                  <span className="text-[#5F5F66]">CALLS {calls}</span>
                  {!avail.ok && (
                    <span
                      data-testid={`${tid}-symbol-${r.symbol}-blocked`}
                      className="px-1 py-0 rounded-sm border border-[#FF3366]/30 bg-[#FF3366]/5 text-[8px] uppercase tracking-[0.12em] text-[#FF3366]"
                    >
                      BLOCKED
                    </span>
                  )}
                </label>
              );
            })}
          </div>

          <div className="grid grid-cols-2 md:grid-cols-3 gap-3 text-[10px] font-mono">
            <label className="flex flex-col">
              <span className="text-[#5F5F66] uppercase tracking-[0.12em] mb-1">Max symbols</span>
              <input
                type="text"
                data-testid={`${tid}-max-symbols`}
                value={maxSymbols}
                onChange={e => setMaxSymbols(e.target.value)}
                className="bg-[#08080A] border border-[#26262B] rounded-sm px-1 py-0.5 text-[#E0E0E0] focus:outline-none"
              />
            </label>
            <label className="flex flex-col">
              <span className="text-[#5F5F66] uppercase tracking-[0.12em] mb-1">Min IVR (optional)</span>
              <input
                type="text"
                data-testid={`${tid}-min-ivr`}
                value={minIvr}
                onChange={e => setMinIvr(e.target.value)}
                placeholder="e.g. 30"
                className="bg-[#08080A] border border-[#26262B] rounded-sm px-1 py-0.5 text-[#E0E0E0] focus:outline-none"
              />
            </label>
            <label className="flex flex-col">
              <span className="text-[#5F5F66] uppercase tracking-[0.12em] mb-1">Freshness threshold</span>
              <span
                data-testid={`${tid}-freshness-threshold`}
                className="text-[#E0E0E0]"
              >
                {staleSecondsCfg}s · warning only · no auto-refresh
              </span>
            </label>
          </div>

          <div className="flex items-center gap-2 flex-wrap">
            <button
              type="button"
              data-testid={`${tid}-run`}
              onClick={onRun}
              disabled={loading}
              className="px-3 py-1 rounded-sm border border-[#22D3CC]/40 bg-[#22D3CC]/10 text-[10px] font-mono uppercase tracking-[0.14em] text-[#22D3CC] hover:bg-[#22D3CC]/20 disabled:opacity-40 inline-flex items-center gap-1"
            >
              <Play size={10} />
              {loading ? "Scanning…" : runLabel}
            </button>
            <button
              type="button"
              data-testid={`${tid}-clear`}
              onClick={onClear}
              className="px-2 py-1 rounded-sm border border-[#26262B] text-[9px] font-mono uppercase tracking-[0.12em] text-[#8A8A93] hover:text-[#E0E0E0] hover:border-[#3F3F47] inline-flex items-center gap-1"
            >
              <Eraser size={10} /> Clear results
            </button>
            {lastScannedAt && (
              <span
                data-testid={`${tid}-last-scanned`}
                className="text-[9px] font-mono uppercase tracking-[0.12em] text-[#5F5F66] inline-flex items-center gap-1"
              >
                <Clock size={9} />
                Last scanned {lastScannedAge != null ? `${lastScannedAge}s` : "—"} ago
              </span>
            )}
          </div>

          {errorMsg && (
            <div
              data-testid={`${tid}-error`}
              className="flex items-start gap-2 text-[10px] font-mono text-[#FF3366] border border-[#FF3366]/30 bg-[#FF3366]/5 px-2 py-1.5 rounded-sm"
            >
              <AlertTriangle size={11} className="shrink-0 mt-0.5" />
              <span>{errorMsg}</span>
            </div>
          )}
        </div>
      )}
    </>
  );
}
